import React, { useEffect, useMemo } from 'react';
import { Button, Card, Form, Select, Typography, message, Space } from 'antd';
import { ArrowLeftOutlined, SaveOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '@/services/api.service';
import assetAccountingDefaultsService from '@/services/asset-accounting-defaults.service';
import { useAuthStore } from '@/store/authStore';

const { Text, Title } = Typography;

interface CoaRow {
  id: string;
  account_code: string;
  account_name: string;
  is_group: boolean;
  account_type: string;
}

const FIELDS: { name: keyof import('@/services/asset-accounting-defaults.service').AssetAccountingDefaultsDto; label: string; hint: string }[] = [
  { name: 'asset_cost_account_id', label: 'Asset cost (FA)', hint: 'Debit on capitalization; credit on disposal' },
  { name: 'inventory_clearing_account_id', label: 'Inventory / GRN clearing', hint: 'Credit when capitalizing draft assets from GRN' },
  { name: 'accumulated_depreciation_account_id', label: 'Accumulated depreciation', hint: 'Credit on monthly depreciation; debit on disposal' },
  { name: 'depreciation_expense_account_id', label: 'Depreciation expense', hint: 'Debit on monthly depreciation (project-tagged)' },
  { name: 'disposal_proceeds_account_id', label: 'Disposal proceeds', hint: 'Bank / AR / clearing on disposal' },
  { name: 'disposal_gain_account_id', label: 'Disposal gain', hint: 'P&L when proceeds exceed NBV' },
  { name: 'disposal_loss_account_id', label: 'Disposal loss', hint: 'P&L when NBV exceeds proceeds' },
];

const AssetAccountingDefaultsPage: React.FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const user = useAuthStore((s) => s.user);
  const canView = Boolean(
    user?.is_super_admin ||
      user?.permissions?.includes('asset.accounting.view') ||
      user?.permissions?.includes('asset.view')
  );
  const canEdit = Boolean(
    user?.is_super_admin ||
      user?.permissions?.includes('asset.accounting.edit') ||
      user?.permissions?.includes('asset.edit')
  );

  const { data: defaults, isLoading } = useQuery({
    queryKey: ['asset-accounting-defaults'],
    queryFn: () => assetAccountingDefaultsService.get(),
    enabled: canView,
  });

  const { data: coa = [] } = useQuery({
    queryKey: ['coa', 'asset-acct'],
    queryFn: async () => {
      const res = await api.get('/finance/chart-of-accounts');
      const data = (res as { data?: unknown })?.data ?? res;
      return Array.isArray((data as { data?: CoaRow[] })?.data)
        ? (data as { data: CoaRow[] }).data
        : Array.isArray(data)
          ? (data as CoaRow[])
          : [];
    },
    enabled: canView,
  });

  const leafAccounts = useMemo(() => coa.filter((a) => !a.is_group), [coa]);

  useEffect(() => {
    if (defaults) {
      form.setFieldsValue(defaults);
    }
  }, [defaults, form]);

  const saveMut = useMutation({
    mutationFn: (body: Record<string, string | null>) => assetAccountingDefaultsService.save(body),
    onSuccess: () => {
      message.success('Asset accounting defaults saved');
      void queryClient.invalidateQueries({ queryKey: ['asset-accounting-defaults'] });
    },
    onError: (e: Error) => message.error(e?.message || 'Save failed'),
  });

  if (!canView) {
    return (
      <div style={{ padding: 24 }}>
        <Card>Access denied</Card>
      </div>
    );
  }

  return (
    <div style={{ maxWidth: 720, margin: '0 auto', padding: 8 }}>
      <Button type="text" icon={<ArrowLeftOutlined />} onClick={() => navigate('/asset/assets')}>
        Back to assets
      </Button>
      <Card style={{ marginTop: 8 }} loading={isLoading}>
        <Title level={4} style={{ marginTop: 0 }}>
          Asset accounting defaults
        </Title>
        <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
          Company-level fallback GL accounts. Prefer configuring accounts on{' '}
          <Button type="link" style={{ padding: 0 }} onClick={() => navigate('/asset/groups')}>
            Asset groups
          </Button>{' '}
          for group-wise posting. Depreciation and disposal resolve: group → these defaults → optional run override.
        </Text>
        <Form
          form={form}
          layout="vertical"
          disabled={!canEdit}
          onFinish={(v) => {
            const body: Record<string, string | null> = {};
            for (const f of FIELDS) {
              body[f.name as string] = v[f.name] ?? null;
            }
            void saveMut.mutate(body);
          }}
        >
          {FIELDS.map((f) => (
            <Form.Item key={f.name as string} name={f.name as string} label={f.label} extra={f.hint}>
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                placeholder="Select leaf account"
                options={leafAccounts.map((a) => ({
                  value: a.id,
                  label: `${a.account_code} — ${a.account_name} (${a.account_type})`,
                }))}
              />
            </Form.Item>
          ))}
          {canEdit && (
            <Space>
              <Button type="primary" htmlType="submit" icon={<SaveOutlined />} loading={saveMut.isPending}>
                Save defaults
              </Button>
            </Space>
          )}
        </Form>
      </Card>
    </div>
  );
};

export default AssetAccountingDefaultsPage;
